import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Icon,
  type IconName,
  type SpacingToken,
} from "@salestrak/ui";
import { useEditor, EditorContent, type Editor, type Range } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import { type Extensions } from "@tiptap/core";
import { EditorToolbar } from "./editor-toolbar";
import { Indent } from "./indent-extension";
import { EntityMention, buildEntitySuggestion } from "./entity-mention";
import { getEnabledMentionSources } from "./mention-sources";
import { useMentionEntityResolver } from "./use-mention-entity-resolver";
import { SlashCommand, filterSlashItems, type SlashItem } from "./slash-command";
import "./editor-styles.css";
import type { Omittable } from "@salestrak/ui";

function hasHeadingLevel(editor: Editor, level: number): boolean {
  const heading = editor.extensionManager.extensions.find(
    (ext) => ext.name === "heading",
  );
  const levels =
    (heading?.options as { levels?: number[] } | undefined)?.levels ?? [];
  return levels.includes(level);
}

type SlashItemDef = SlashItem & Omittable<{
  // Capability gate: only offer the item when the command is registered —
  // invoking an unregistered command throws (it's undefined on the chain).
  available?: (editor: Editor) => boolean;
}>;

const insertTrigger =
  (char: string) => (editor: Editor, range: Range) =>
    editor.chain().focus().deleteRange(range).insertContent(char).run();
const block =
  (fn: (e: Editor) => void) => (editor: Editor, range: Range) => {
    editor.chain().focus().deleteRange(range).run();
    fn(editor);
  };

const slashTile = (name: IconName) => () => (
  <Box
    width={26}
    height={26}
    border
    rounded="md"
    direction="column"
    align="center"
    justify="center"
    shrink={false}
  >
    <Icon name={name} size="xs" color="muted" />
  </Box>
);

const SLASH_ITEMS: SlashItemDef[] = [
  {
    id: "text-normal",
    title: "Normal text",
    section: "Text",
    renderAvatar: slashTile("type"),
    run: block((e) => e.chain().focus().setParagraph().run()),
  },
  {
    id: "text-h1",
    title: "Heading 1",
    section: "Text",
    renderAvatar: slashTile("heading-1"),
    available: (e) => hasHeadingLevel(e, 1),
    run: block((e) => e.chain().focus().toggleHeading({ level: 1 }).run()),
  },
  {
    id: "text-h2",
    title: "Heading 2",
    section: "Text",
    renderAvatar: slashTile("heading-2"),
    available: (e) => hasHeadingLevel(e, 2),
    run: block((e) => e.chain().focus().toggleHeading({ level: 2 }).run()),
  },
  {
    id: "text-h3",
    title: "Heading 3",
    section: "Text",
    renderAvatar: slashTile("heading-3"),
    available: (e) => hasHeadingLevel(e, 3),
    run: block((e) => e.chain().focus().toggleHeading({ level: 3 }).run()),
  },
  {
    id: "text-h4",
    title: "Heading 4",
    section: "Text",
    renderAvatar: slashTile("heading-4"),
    available: (e) => hasHeadingLevel(e, 4),
    run: block((e) => e.chain().focus().toggleHeading({ level: 4 }).run()),
  },
  {
    id: "list-bullet",
    title: "Bulleted list",
    section: "Lists",
    renderAvatar: slashTile("list"),
    run: block((e) => e.chain().focus().toggleBulletList().run()),
  },
  {
    id: "list-ordered",
    title: "Numbered list",
    section: "Lists",
    renderAvatar: slashTile("list-ordered"),
    run: block((e) => e.chain().focus().toggleOrderedList().run()),
  },
  {
    id: "list-task",
    title: "Checklist",
    section: "Lists",
    renderAvatar: slashTile("list-checks"),
    available: (e) => "toggleTaskList" in e.commands,
    run: block((e) => e.chain().focus().toggleTaskList().run()),
  },
  {
    id: "block-quote",
    title: "Quote",
    section: "Blocks",
    renderAvatar: slashTile("quote"),
    run: block((e) => e.chain().focus().toggleBlockquote().run()),
  },
  {
    id: "block-code",
    title: "Code block",
    section: "Blocks",
    renderAvatar: slashTile("square-code"),
    run: block((e) => e.chain().focus().toggleCodeBlock().run()),
  },
  {
    id: "block-divider",
    title: "Divider",
    section: "Blocks",
    renderAvatar: slashTile("minus"),
    run: block((e) => e.chain().focus().setHorizontalRule().run()),
  },
  {
    id: "block-table",
    title: "Table",
    section: "Blocks",
    renderAvatar: slashTile("table"),
    available: (e) => "insertTable" in e.commands,
    run: block((e) =>
      e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
    ),
  },
];

function buildMentionSlashItems(): SlashItemDef[] {
  return getEnabledMentionSources().map((source) => ({
    id: `mention-${source.kind}`,
    title: source.slash.title,
    section: "Mention",
    renderAvatar: slashTile(source.slash.icon),
    run: insertTrigger(source.char),
  }));
}

// Builds the "/photo" item against the host's picker (file prompt + upload).
// The pick/upload is async, so `run` kicks the flow off and inserts the image
// node when the object URL arrives.
function buildPhotoSlashItem(
  pickPhoto: () => Promise<{ objectUrl: string } | null>,
): SlashItemDef {
  return {
    id: "block-photo",
    title: "Photo",
    section: "Blocks",
    renderAvatar: slashTile("image"),
    available: (e) => "setImage" in e.commands,
    run: (editor, range) => {
      editor.chain().focus().deleteRange(range).run();
      void pickPhoto().then((result) => {
        if (!result || editor.isDestroyed) return;
        editor.chain().focus().setImage({ src: result.objectUrl }).run();
      });
    },
  };
}

function getSlashItems(
  query: string,
  editor: Editor,
  pickPhoto?: () => Promise<{ objectUrl: string } | null>,
): SlashItem[] {
  const items = [
    ...SLASH_ITEMS,
    ...(pickPhoto ? [buildPhotoSlashItem(pickPhoto)] : []),
    ...buildMentionSlashItems(),
  ];
  return filterSlashItems(
    items.filter((item) => item.available?.(editor) ?? true),
    query,
  );
}

// Appended to the consumer's placeholder prefix when `shortcutHints` is on.
const SHORTCUT_HINT = " or type '/' for commands...";

/**
 * Compose a placeholder with the shortcut-hint suffix — the exact string the
 * editor shows when `shortcutHints` is on. Lets lazily-mounted composers (which
 * render a plain placeholder before TipTap mounts) match the editor's wording
 * from one source of truth.
 */
export function withShortcutHints(placeholder: string): string {
  return `${placeholder}${SHORTCUT_HINT}`;
}

type RichTextEditorProps = Omittable<{
  content: string;
  onUpdate: (html: string) => void;
  /** Placeholder prefix shown in an empty editor. Pair with `shortcutHints`. */
  placeholder?: string;
  /** Append " or type '/' for commands..." to `placeholder`. */
  shortcutHints?: boolean;
  editable?: boolean;
  mentions?: boolean;
  /** Compact mode: fewer extensions and a slimmer toolbar for note/comment editing */
  compact?: boolean;
  /** Auto-focus the editor when it mounts */
  autoFocus?: boolean;
  /** Padding around the editable content only. The in-flow mobile toolbar stays
   *  flush to the container edges, so consumers pass their inset here rather than
   *  wrapping the editor (which would inset the toolbar too). */
  contentPaddingX?: SpacingToken;
  contentPaddingY?: SpacingToken;
  /**
   * Host-provided photo picker (file prompt + upload). When set, a "Photo"
   * slash item is offered; the resolved object URL is inserted as an inline
   * image node. The host owns persistence (rewriting srcs, linking ids).
   */
  onPickPhoto?: () => Promise<{ objectUrl: string } | null>;
}>;

export function RichTextEditor({
  content,
  onUpdate,
  placeholder = "Write",
  shortcutHints = false,
  editable = true,
  mentions = false,
  compact = false,
  autoFocus = false,
  contentPaddingX,
  contentPaddingY,
  onPickPhoto,
}: RichTextEditorProps) {
  // Extensions are built once, so the resolver flows through a ref.
  const resolveEntity = useMentionEntityResolver();
  const resolveEntityRef = useRef(resolveEntity);
  resolveEntityRef.current = resolveEntity;
  const onPickPhotoRef = useRef(onPickPhoto);
  onPickPhotoRef.current = onPickPhoto;

  const resolvedPlaceholder = shortcutHints
    ? withShortcutHints(placeholder)
    : placeholder;

  const extensions = useMemo<Extensions>(() => {
    const linkOptions = {
      openOnClick: false,
      HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
    };

    const base: Extensions = compact
      ? [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            link: linkOptions,
          }),
          // Notes embed photos via the "/photo" slash item, so the compact
          // editor needs the image node too.
          Image.configure({ inline: false, allowBase64: true }),
          Placeholder.configure({
            placeholder: ({ node }) =>
              node.type.name === "paragraph" ? resolvedPlaceholder : "",
          }),
        ]
      : [
          StarterKit.configure({
            heading: { levels: [1, 2, 3, 4] },
            link: linkOptions,
          }),
          TextStyle,
          Color,
          TextAlign.configure({
            types: ["heading", "paragraph"],
            alignments: ["left", "center", "right"],
          }),
          Indent,
          Image.configure({ inline: false, allowBase64: true }),
          Table.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
          TaskList,
          TaskItem.configure({ nested: true }),
          Placeholder.configure({
            placeholder: ({ node }) =>
              node.type.name === "paragraph" ? resolvedPlaceholder : "",
          }),
        ];

    if (mentions) {
      base.push(
        EntityMention.configure({
          HTMLAttributes: { class: "mention" },
          resolveEntity: (ref) => resolveEntityRef.current(ref),
          suggestions: getEnabledMentionSources().map(buildEntitySuggestion),
        }),
        SlashCommand.configure({
          getItems: (query: string, editor: Editor) =>
            getSlashItems(query, editor, onPickPhotoRef.current),
        }),
      );
    }

    return base;
     
  }, [compact, resolvedPlaceholder, mentions]);

  const editor = useEditor({
    extensions,
    content,
    editable,
    onUpdate: ({ editor: ed }) => {
      onUpdate(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: "focus:outline-none",
      },
      transformPastedHTML(html) {
        return (
          html
            .replace(/<style[\s\S]*?<\/style>/gi, "")
            .replace(/<(meta|link)[^>]*>/gi, "")
            .replace(/<!--[\s\S]*?-->/g, "")
            // Strip inline presentation attrs the server allow-list won't persist
            // so what TipTap keeps matches the sanitizer's round-trip.
            .replace(
              /\s(style|class|face|color|size|bgcolor|align)="[^"]*"/gi,
              "",
            )
            .replace(
              /\s(style|class|face|color|size|bgcolor|align)='[^']*'/gi,
              "",
            )
        );
      },
    },
  });

  // The tiptap-* classes scope editor-styles.css to this subtree — a CSS
  // hook for a React-unmanaged DOM tree, not chrome (tracked className).
  const cssClass = ["tiptap-editor", compact && "tiptap-compact"]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  // NodeView chips don't re-render when the workspace maps (and thus the
  // resolver) hydrate, so when the resolver identity changes dispatch a no-op,
  // non-undoable transaction to re-run node views with the resolvable data.
  const lastResolverRef = useRef(resolveEntity);
  useEffect(() => {
    if (!editor) return;
    if (lastResolverRef.current === resolveEntity) return;
    lastResolverRef.current = resolveEntity;
    editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
  }, [editor, resolveEntity]);

  useEffect(() => {
    if (!autoFocus || !editor) return;
    // Guard against a destroyed editor: @tiptap/react destroys via a deferred
    // setTimeout that can beat a pending (throttled) rAF, nulling commandManager.
    const frame = requestAnimationFrame(() => {
      if (editor.isDestroyed) return;
      editor.commands.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [autoFocus, editor]);

  if (!editor) return null;

  const toolbar = editable ? (
    <EditorToolbar editor={editor} compact={compact} />
  ) : null;

  return (
    <Box className={cssClass}>
      {toolbar}
      <Box paddingX={contentPaddingX} paddingY={contentPaddingY}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
